import { useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../../shared/components/Button';
import { describeError, describeExportError } from '../describeError';
import { useIsMounted } from '../hooks/useIsMounted';
import { payrollHistoryImportGateway as gateway } from '../index';
import { NEW_IMPORT_PATH } from '../routes';
import { saveDownloadedFile } from '../saveBlob';
import type { Capabilities, DownloadedFile, TemplateKind } from '../types';
import { ErrorSummary } from './basics';

type Busy = 'standard-template' | 'standard-example' | 'export' | null;

/**
 * The Import / Export menu: the five things a person can do with payroll history. Importing needs create permission;
 * everything else needs read (which the page already requires). Downloads go through the authenticated gateway.
 */
export function HubMenu({ capabilities }: { capabilities: Capabilities }) {
  const mounted = useIsMounted();
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState('');

  const run = async (which: Exclude<Busy, null>, fetchFile: () => Promise<DownloadedFile>, failure: (err: unknown) => string) => {
    setBusy(which);
    setError(null);
    setMessage('');
    try {
      const file = await fetchFile();
      saveDownloadedFile(file);
      if (mounted.current) setMessage(`Downloaded ${file.fileName}.`);
    } catch (err) {
      if (mounted.current) setError(failure(err));
    } finally {
      if (mounted.current) setBusy(null);
    }
  };

  const template = (kind: TemplateKind) => (): Promise<DownloadedFile> => gateway.downloadTemplate(kind);
  const downloadFailure = (err: unknown) => describeError(err, 'Could not download this file.');

  const goToHistory = (event: MouseEvent<HTMLAnchorElement>) => {
    const heading = document.getElementById('phi-history-heading');
    if (!heading) return;
    event.preventDefault();
    heading.scrollIntoView?.({ block: 'start' });
    heading.focus();
  };

  const canImport = capabilities.canCreate && capabilities.enabled;

  return (
    <section aria-labelledby="phi-hub-heading">
      <h3 id="phi-hub-heading">What would you like to do?</h3>
      {error && <ErrorSummary message={error} />}
      <div role="status" aria-live="polite" className="phi-sr-only">{message}</div>
      <ul className="phi-menu">
        {canImport && (
          <li className="phi-menu-item" data-testid="hub-import">
            <Link to={NEW_IMPORT_PATH} className="phi-btn-primary" aria-describedby="phi-hub-import-desc">Import Payroll History</Link>
            <p id="phi-hub-import-desc">
              Bring in payroll history from Escochex Payroll, the EPN Excel template, or a spreadsheet or CSV from another system.
            </p>
          </li>
        )}
        <li className="phi-menu-item" data-testid="hub-download-template">
          <Button
            type="button" className="phi-btn-secondary" aria-describedby="phi-hub-template-desc"
            loading={busy === 'standard-template'} loadingLabel="Downloading…" disabled={busy !== null && busy !== 'standard-template'}
            onClick={() => void run('standard-template', template('standard-template'), downloadFailure)}
          >
            Download EPN Payroll History Template
          </Button>
          <p id="phi-hub-template-desc">Get the blank EPN Excel workbook to fill in with your payroll history.</p>
        </li>
        <li className="phi-menu-item" data-testid="hub-download-example">
          <Button
            type="button" className="phi-btn-secondary" aria-describedby="phi-hub-example-desc"
            loading={busy === 'standard-example'} loadingLabel="Downloading…" disabled={busy !== null && busy !== 'standard-example'}
            onClick={() => void run('standard-example', template('standard-example'), downloadFailure)}
          >
            Download Completed Example
          </Button>
          <p id="phi-hub-example-desc">See a finished example of the EPN Excel workbook, filled in with fictitious data.</p>
        </li>
        <li className="phi-menu-item" data-testid="hub-review-history">
          <a href="#phi-history-heading" className="phi-btn-secondary" aria-describedby="phi-hub-history-desc" onClick={goToHistory}>
            Review Import History
          </a>
          <p id="phi-hub-history-desc">See every import that has been started and where it stands.</p>
        </li>
        <li className="phi-menu-item" data-testid="hub-export">
          <Button
            type="button" className="phi-btn-secondary" aria-describedby="phi-hub-export-desc"
            loading={busy === 'export'} loadingLabel="Preparing export…" disabled={busy !== null && busy !== 'export'}
            onClick={() => void run('export', () => gateway.exportHistory(), describeExportError)}
          >
            Export EPN Payroll History
          </Button>
          <p id="phi-hub-export-desc">Download your imported payroll history as an Excel file.</p>
        </li>
      </ul>
    </section>
  );
}
